import { dateUtils } from '@/utils'
import { CalendarConfig, RecurrenceWhen, type RecurrenceSettingsState } from '../../../types'
import { addDays, isBefore } from 'date-fns'
import { getDay, isAfter, isSameMonth, subDays } from 'date-fns/esm'

export function updateRecurrenceWhen(
  state: RecurrenceSettingsState,
  recurrenceWhen: RecurrenceWhen,
  calendarConfig: CalendarConfig,
): RecurrenceSettingsState {
  return {
    ...state,
    value: {
      ...state.value,
      recurrencePeriod: updateRecurrencePeriodFromRecurrenceWhen(
        state.value.recurrencePeriod,
        recurrenceWhen,
        calendarConfig,
      ),
    },
  }
}

function updateRecurrencePeriodFromRecurrenceWhen(
  recurrencePeriod: uui.domain.ui.forms.RecurrencePeriod,
  recurrenceWhen: RecurrenceWhen,
  calendarConfig: CalendarConfig,
): uui.domain.ui.forms.RecurrencePeriod {
  switch (recurrenceWhen.type) {
    case 'WEEKLY': {
      const dateWithWeekday = computeDateWithWeekday(
        calendarConfig,
        recurrenceWhen.weekday,
        recurrencePeriod,
      )

      return {
        ...recurrencePeriod,
        date: dateUtils.convertToYYYYMMDD(dateWithWeekday),
        weekday: recurrenceWhen.weekday as uui.domain.client.rm.WeekDay,
      }
    }

    case 'MONTHLY': {
      return {
        ...recurrencePeriod,
        date: recurrenceWhen.date,
        cardinal: recurrenceWhen.cardinal as uui.domain.client.rm.WeekOfMonth,
        weekday: recurrenceWhen.weekday as uui.domain.client.rm.WeekDay,
      }
    }

    case 'ANNUALLY': {
      return {
        ...recurrencePeriod,
        date: recurrenceWhen.date,
        cardinal: recurrenceWhen.cardinal as uui.domain.client.rm.WeekOfMonth,
        month: recurrenceWhen.month as uui.domain.client.rm.MonthNumber,
        weekday: recurrenceWhen.weekday as uui.domain.client.rm.WeekDay,
      }
    }
  }
}

function computeDateWithWeekday(
  calendarConfig: CalendarConfig,
  weekday: number,
  recurrencePeriod: uui.domain.ui.forms.RecurrencePeriod,
): Date {
  function isDateDisabled(date: Date): boolean {
    const dateKey = dateUtils.convertToYYYYMMDD(date)
    return calendarConfig.disabledDays[dateKey] !== undefined
  }

  const minDate = dateUtils.parseDate(calendarConfig.minDate)
  const maxDate = dateUtils.parseDate(calendarConfig.maxDate)
  const currentDate = dateUtils.parseDate(recurrencePeriod.date)
  const currentDateDay = getDay(currentDate)
  const daysDelta = weekday - currentDateDay

  const preferredDate =
    daysDelta < 0 ? subDays(currentDate, Math.abs(daysDelta)) : addDays(currentDate, daysDelta)

  const lessPreferredDate =
    daysDelta < 0 ? addDays(currentDate, 7 + daysDelta) : subDays(currentDate, 7 - daysDelta)

  const preferredDateEligible =
    isAfter(preferredDate, minDate) &&
    isBefore(preferredDate, maxDate) &&
    !isDateDisabled(preferredDate)

  const lessPreferredDateEligible =
    isAfter(lessPreferredDate, minDate) &&
    isBefore(lessPreferredDate, maxDate) &&
    !isDateDisabled(lessPreferredDate)

  const preferredDateScore = getDateScore(
    preferredDateEligible,
    isSameMonth(preferredDate, currentDate),
  )

  const lessPreferredDateScore = getDateScore(
    lessPreferredDateEligible,
    isSameMonth(lessPreferredDate, currentDate),
  )

  if (preferredDateScore === lessPreferredDateScore) {
    console.error('No preferred date found for weekday')
    return currentDate
  }

  return preferredDateScore >= lessPreferredDateScore ? preferredDate : lessPreferredDate
}

function getDateScore(eligible: boolean, isInTheSameMonth: boolean): number {
  return eligible ? 1 : 0 * (isInTheSameMonth ? 50 : 0)
}
